import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSessionCleanup, useSessionStateStore } from '@renderer/stores/sessionState'
import { useHomePath } from '@renderer/hooks/useHomePath'
import { useCountdown } from '@renderer/utils/countdownUtils'
import QRCode from 'qrcode'
import { useConfigStore } from '@renderer/stores/configState'
import { resolveGallerySessionUrl } from '@renderer/utils/galleryUrlUtils'
import { FaStar } from 'react-icons/fa'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { useTranslation } from 'react-i18next'
import NewspaperConsentModal from './components/NewspaperConsentModal'
import SessionFeedbackModal, {
  SessionFeedbackButton
} from '@renderer/components/SessionFeedbackModal'
import { useSessionFeedback } from '@renderer/hooks'
import NewspaperLayout from './components/NewspaperLayout'

const ResultPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    gallery,
    id,
    timeline,
    setSessionState,
    uploadSessionState,
    mode,
    logActivity,
    userPreferences
  } = useSessionStateStore()
  const sessionCleanup = useSessionCleanup()
  // Hub members return to the HUB Welcome, not their own.
  const homePath = useHomePath()
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.result
  const [stripPreview, setStripPreview] = useState('')
  const [videoPreview, setVideoPreview] = useState('')
  const [qrCodeImage, setQrCodeImage] = useState('')
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const [satisfactionRating, setSatisfactionRating] = useState<number | null>(
    userPreferences.satisfactionRating || null
  )
  const [hoveredStar, setHoveredStar] = useState<number | null>(null)

  const askUserConsent =
    config?.settings?.askUserConsent === undefined ? true : config?.settings?.askUserConsent

  const [showConsent, setShowConsent] = useState(askUserConsent)
  const hasCompleted = useRef(false)

  const resultCountdown = useCountdown(config?.settings.resultTimeout || 120)
  /**
   * The detail behind the star. Opens itself on 1-2 stars, and stays an offer
   * above that — see lib/feedback/aspects.ts for why those six aspects.
   */
  const feedback = useSessionFeedback(satisfactionRating)

  // Hold the result timer while the survey is open, or the booth resets
  // mid-answer and the guest's taps go nowhere.
  useEffect(() => {
    if (feedback.isOpen) {
      resultCountdown.stop()
    } else if (!showConsent) {
      resultCountdown.start()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback.isOpen])

  const handleConsent = async (consent: boolean): Promise<void> => {
    setShowConsent(false)
    resultCountdown.start()

    setSessionState({
      userConsent: {
        photoPublication: consent,
        consentTimestamp: new Date()
      }
    })

    logActivity(`User ${consent ? 'granted' : 'declined'} photo publication consent`, {
      consent,
      timestamp: new Date()
    })

    // CRITICAL: Wait for state to propagate, then upload to ensure consent is saved
    await new Promise((resolve) => setTimeout(resolve, 100))
    const currentState = useSessionStateStore.getState()

    // Verify consent was set correctly
    if (currentState.userConsent?.photoPublication !== consent) {
      console.warn('[CONSENT] Consent value mismatch, retrying state update')
      setSessionState({
        userConsent: {
          photoPublication: consent,
          consentTimestamp: new Date()
        }
      })
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    // Upload session state with consent
    await uploadSessionState(id)
    console.log('[CONSENT] Uploaded consent:', consent)
  }

  const handleRating = (rating: number): void => {
    if (satisfactionRating === rating) {
      setSatisfactionRating(null)
      setSessionState({
        userPreferences: {
          satisfactionRating: null,
          satisfactionTimestamp: null
        }
      })
      logActivity(`User deselected satisfaction rating`)
    } else {
      setSatisfactionRating(rating)
      const timestamp = new Date()
      setSessionState({
        userPreferences: {
          satisfactionRating: rating,
          satisfactionTimestamp: timestamp
        }
      })
      logActivity(`User rated session satisfaction: ${rating} stars`, {
        rating,
        timestamp
      })
    }
  }

  // Watch for gallery updates to catch strip when it's added
  // Use the most recent strip (filtered strip created in Select.tsx after filtering)
  useEffect(() => {
    if (gallery.capturedItems && gallery.capturedItems.length > 0) {
      if (mode === 'record') {
        const videoItem = gallery.capturedItems.find((item) => item.videoUrl)
        if (videoItem?.videoUrl && !videoPreview) {
          setVideoPreview(videoItem.videoUrl)
        }
      } else {
        // Find all strip items and use the most recent one (should be filtered if Filter page was used)
        const stripItems = gallery.capturedItems.filter((item) =>
          item.id?.includes('dreambooth-strip') && item.stripUrl
        )
        // Sort by timestamp descending to get most recent, or use last item if no timestamps
        const mostRecentStrip = stripItems.length > 0
          ? stripItems.sort((a, b) => {
              const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0
              const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0
              return timeB - timeA
            })[0]
          : null
        if (mostRecentStrip?.stripUrl && !stripPreview) {
          setStripPreview(mostRecentStrip.stripUrl)
        }
      }
    }
  }, [gallery.capturedItems, mode, videoPreview, stripPreview])

  useEffect(() => {
    resultCountdown.setOnTick(() => {
      setRemainingTime(resultCountdown.getFormattedRemainingSeconds())
    })

    resultCountdown.setOnComplete(async () => {
      if (hasCompleted.current) {
        logActivity(`Result page timed out after ${config?.settings.resultTimeout || 120} seconds.`)
        await new Promise((resolve) => setTimeout(resolve, 100))
        const currentState = useSessionStateStore.getState()

        if (currentState.timeline.isCompleted !== true) {
          console.warn('[RESULT] isCompleted not set on timeout, setting it now')
          setSessionState({
            timeline: {
              isCompleted: true
            }
          })
          await new Promise((resolve) => setTimeout(resolve, 100))
        }

        await uploadSessionState(id).finally(() => {
          sessionCleanup()
          // Navigate to root page (welcome page)
          navigate(homePath)
        })
      }
    })

    const checkVideoProcessing = async (): Promise<void> => {
      const videoProcessingId = gallery.capturedItems.find(
        (item) => item.id?.includes('video-') && backgroundVideoProcessor.isTaskProcessing(item.id)
      )?.id

      if (videoProcessingId) {
        console.log('Waiting for video processing to complete:', videoProcessingId)
        await new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            if (!backgroundVideoProcessor.isTaskProcessing(videoProcessingId)) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 1000)
        })
      }
    }

    const setupResult = async (): Promise<void> => {
      if (hasCompleted.current) return
      hasCompleted.current = true

      await checkVideoProcessing()

      // Get latest gallery state
      const currentGallery = useSessionStateStore.getState().gallery
      let finalStripPreview = ''
      let finalVideoPreview = ''

      if (currentGallery.capturedItems && currentGallery.capturedItems.length > 0) {
        if (mode === 'record') {
          const videoItem = currentGallery.capturedItems.find((item) => item.videoUrl)
          finalVideoPreview = videoItem?.videoUrl || ''
          if (finalVideoPreview) {
            setVideoPreview(finalVideoPreview)
          }
        } else {
          // Find all strip items and use the most recent one (should be filtered if Filter page was used)
          const stripItems = currentGallery.capturedItems.filter(
            (item) => item.id?.includes('dreambooth-strip') && item.stripUrl
          )
          // Sort by timestamp descending to get most recent, or use last item if no timestamps
          const mostRecentStrip =
            stripItems.length > 0
              ? stripItems.sort((a, b) => {
                  const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0
                  const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0
                  return timeB - timeA
                })[0]
              : null
          finalStripPreview = mostRecentStrip?.stripUrl || ''
          if (finalStripPreview) {
            setStripPreview(finalStripPreview)
          }
        }
      }

      logActivity(`Session completed!`, {
        mode,
        stripUrl: finalStripPreview,
        videoUrl: finalVideoPreview
      })

      const endTime = new Date()
      const duration = timeline?.startTime
        ? endTime.getTime() - new Date(timeline.startTime).getTime()
        : null

      setSessionState({
        timeline: {
          endTime: endTime,
          duration: duration,
          isCompleted: true
        }
      })

      await new Promise((resolve) => setTimeout(resolve, 100))

      let currentState = useSessionStateStore.getState()
      if (currentState.timeline.isCompleted !== true) {
        console.warn('[RESULT] isCompleted not set in state, retrying state update')
        setSessionState({
          timeline: {
            isCompleted: true,
            endTime: endTime,
            duration: duration
          }
        })
        await new Promise((resolve) => setTimeout(resolve, 100))
        currentState = useSessionStateStore.getState()
      }

      console.log('[RESULT] Uploading session with isCompleted:', currentState.timeline.isCompleted)

      // Only upload if consent is not required OR consent has been given
      // This prevents uploading without consent data when askUserConsent is true
      if (!askUserConsent) {
        await uploadSessionState(id)
      } else {
        console.log('[RESULT] Skipping initial upload - waiting for user consent')
      }

      const galleryTarget = resolveGallerySessionUrl(id, config?.settings)
      QRCode.toDataURL(galleryTarget, {
        margin: 0,
        width: 600
      }).then(setQrCodeImage)

      // Start result countdown after setup (if consent not required or already handled)
      if (!askUserConsent || !showConsent) {
        resultCountdown.start()
      }
    }

    setupResult()

    return (): void => {
      resultCountdown.stop()
    }
  }, [])

  return (
    <>
      <SessionFeedbackModal
        isOpen={feedback.isOpen}
        rating={satisfactionRating}
        answers={feedback.answers}
        onAnswer={feedback.setAnswer}
        answeredCount={feedback.answeredCount}
        onSubmit={feedback.submit}
        onClose={feedback.close}
      />

      {askUserConsent && (
        <NewspaperConsentModal
          isVisible={showConsent}
          onConsent={handleConsent}
          stripPreview={mode === 'record' ? videoPreview : stripPreview}
          mode={mode}
        />
      )}

      {!showConsent && (
        <NewspaperLayout>
          <div className="flex flex-col h-full relative overflow-hidden">
            {/* Title Section */}
            <div className="flex-shrink-0 w-full border-2 border-double border-[#2a2a2a] bg-newspaper-white p-4 md:p-6">
              <div className="border-t-2 border-[#2a2a2a] my-2 md:my-4 mb-1 md:mb-2"></div>
              <div className="font-newspaper font-bold text-3xl md:text-5xl lg:text-7xl leading-tight uppercase tracking-wider text-[#1a1a1a] text-center py-1 md:py-2">
                {mode === 'record'
                  ? t('result.videoReady', 'EXTRA! EXTRA! YOUR VIDEO IS READY!')
                  : t('result.photosReady', 'EXTRA! EXTRA! YOUR PHOTOS ARE READY!')}
              </div>
              <div className="border-t-2 border-[#2a2a2a] my-2 md:my-4 mt-1 md:mt-2"></div>
            </div>

            {/* Main Content: Strip + QR/Rating (Stacked Vertically) */}
            <div className="flex-1 min-h-0 overflow-hidden">
              <div className="w-full flex flex-col h-full bg-newspaper-white items-center justify-center px-4 md:px-8 py-4 md:py-6 gap-4 md:gap-6">
                {/* Strip Section - Takes available space */}
                <div className="flex-1 min-h-0 flex items-center justify-center w-full">
                  {mode === 'record' && videoPreview ? (
                    <video
                      src={videoPreview}
                      controls
                      className="max-h-full max-w-full w-auto h-auto object-contain rounded-none"
                    />
                  ) : stripPreview ? (
                    <img
                      className="max-h-full max-w-full w-auto h-auto object-contain"
                      src={stripPreview || ''}
                      alt={t('result.photoStripAlt', 'Photo Strip')}
                      crossOrigin="anonymous"
                    />
                  ) : null}
                </div>

                {/* QR Code + Session ID + Rating Section */}
                <div className="flex-shrink-0 flex flex-col items-center justify-center gap-3 md:gap-4">
                  {/* QR Code Section */}
                  <div className="flex flex-col items-center gap-2 md:gap-3">
                    <p className="text-newspaper-black text-sm md:text-base lg:text-lg font-newspaper text-center">
                      {t('result.scanQrInstruction', 'Scan the QR code to access your {{type}}', {
                        type:
                          mode === 'record'
                            ? t('result.video', 'video')
                            : t('result.digitalPhotos', 'digital photos')
                      })}
                    </p>
                    <div className="bg-newspaper-white border-2 border-double border-[#2a2a2a] p-2 md:p-3 rounded-none shadow-none flex items-center justify-center">
                      {qrCodeImage ? (
                        <img
                          className="w-[150px] h-[150px] md:w-[200px] md:h-[200px] lg:w-[250px] lg:h-[250px]"
                          src={qrCodeImage}
                          alt={t('result.qrCodeAlt', 'QR Code')}
                        />
                      ) : (
                        <div className="w-[150px] h-[150px] md:w-[200px] md:h-[200px] lg:w-[250px] lg:h-[250px] flex items-center justify-center">
                          <svg
                            className="w-12 h-12 md:w-16 md:h-16 animate-spin text-[#2a2a2a]/40"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-newspaper-black text-xs md:text-sm lg:text-base font-newspaper text-center">
                      {t('result.sessionId', 'Session ID:')}
                      <span className="text-newspaper-black ml-2 font-mono text-sm md:text-base lg:text-lg font-bold">
                        {id}
                      </span>
                    </p>
                  </div>

                  {/* Satisfaction Rating Section */}
                  <div className="flex flex-col items-center">
                    <p className="text-newspaper-black text-sm md:text-base lg:text-lg font-newspaper mb-2 md:mb-3 text-center">
                      {t('result.satisfaction.rateExperience', 'Rate your experience')}
                    </p>
                    <div className="flex gap-1 md:gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => handleRating(star)}
                          onMouseEnter={() => setHoveredStar(star)}
                          onMouseLeave={() => setHoveredStar(null)}
                          className="transition-all duration-200 hover:scale-110 text-2xl md:text-3xl lg:text-4xl p-1"
                          title={t('result.satisfaction.starAlt', 'Rate {{rating}} stars', {
                            rating: star
                          })}
                        >
                          <FaStar
                            className={`${
                              (hoveredStar && star <= hoveredStar) ||
                              (satisfactionRating && star <= satisfactionRating)
                                ? 'text-yellow-400'
                                : 'text-gray-300'
                            } transition-colors duration-200`}
                          />
                        </button>
                      ))}
                    </div>
                    {satisfactionRating && (
                      <div className="text-center animate-fade-in mt-1 md:mt-2">
                        <p className="text-xs md:text-sm lg:text-base text-green-600 font-medium font-newspaper">
                          {feedback.submitted
                            ? t('result.feedback.thanks')
                            : t('result.satisfaction.thankYou', 'Thank you for your feedback!')}
                        </p>
                        <SessionFeedbackButton
                          onClick={feedback.open}
                          submitted={feedback.submitted}
                          className="text-xs md:text-sm text-[#2a2a2a] font-newspaper"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Bottom Section: Countdown + Back to Home Button */}
            <div className="flex-shrink-0 w-full flex flex-row items-center justify-center gap-4 pt-2 pb-3 px-6 md:px-8 bg-newspaper-white border-t-2 border-double border-[#2a2a2a]">
              {/* Centered: Countdown + Back to Home Button */}
              <div className="flex flex-col items-center gap-3 md:gap-4">
                {/* Countdown */}
                {(config?.settings?.resultTimeout || 0) > 0 && (
                  <div className="text-center">
                    <div className="font-bold text-newspaper-black text-2xl md:text-3xl font-newspaper">
                      {remainingTime}
                    </div>
                  </div>
                )}
                {/* Back to Home Button */}
                <button
                  type="button"
                  onClick={async () => {
                    resultCountdown.stop()
                    await new Promise((resolve) => setTimeout(resolve, 100))
                    const currentState = useSessionStateStore.getState()

                    if (currentState.timeline.isCompleted !== true) {
                      console.warn('[RESULT] isCompleted not set on button click, setting it now')
                      setSessionState({
                        timeline: {
                          isCompleted: true
                        }
                      })
                      await new Promise((resolve) => setTimeout(resolve, 100))
                    }

                    await uploadSessionState(id).finally(() => {
                      sessionCleanup()
                      // Navigate to root page (welcome page)
                      navigate(homePath)
                    })
                  }}
                  className="border-2 border-double border-[#2a2a2a] font-newspaper font-bold transition-all duration-200 hover:opacity-90 text-3xl md:text-4xl bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none px-8 md:px-12 py-4 md:py-5 min-w-[200px] md:min-w-[300px] tracking-wider"
                >
                  {t('result.backToHome', 'Back to Home')}
                </button>
              </div>
            </div>
          </div>
        </NewspaperLayout>
      )}
    </>
  )
}

export default ResultPage
